import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Footer } from 'components/Footer';
import Header from 'components/Header';
import { useToast } from 'components/toast/ToastProvider';
import { PATHS } from 'routes/paths';
import { authService } from 'services/auth/authService';
import { getFullName } from 'utils/auth';
import { getErrorMessage } from 'utils/http';
import { authStorage } from 'utils/storage';
import { isValidEmail, isValidPassword } from 'utils/validation';

type Errors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
};

export default function SignUpPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { showError, showSuccess } = useToast();

  const stripedBg = {
    backgroundImage:
      'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)',
  };

  const validate = () => {
    const newErrors: Errors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isValidPassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((current) => ({ ...current, [name]: value }));

    if (errors[name as keyof Errors]) {
      setErrors((current) => ({ ...current, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      showError('Please fix the highlighted signup fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authService.signup({
        name: getFullName(formData.firstName, formData.lastName),
        email: formData.email,
        password: formData.password,
      });

      authStorage.setToken(response.data.token);
      setSuccessMessage(response.message || 'Signup successful');
      setIsSuccess(true);
      showSuccess(response.message || 'Signup successful');
    } catch (error) {
      showError(getErrorMessage(error, 'Unable to create your account right now.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={stripedBg}
      className="md:pt-8 md:pb-8 min-h-screen w-full py-2 px-2 sm:px-8 lg:px-12 flex justify-center font-sans text-slate-900 overflow-hidden"
    >
      <div
        className="w-full bg-white rounded-[2.5rem] overflow-hidden border border-gray-200 flex flex-col shadow-sm"
        style={{ boxShadow: 'var(--shadow-box)' }}
      >
        <Header />

        <main className="flex-grow flex items-center justify-center py-20 px-4">
          <div className="w-full max-w-lg bg-white rounded-3xl border border-gray-200 p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {isSuccess ? (
              <div className="text-center py-2">
                <div className="w-16 h-16 bg-[#2b3543]/10 text-[#2b3543] rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Account Created!</h2>
                <p className="text-slate-500 font-medium mb-4">
                  Your account is ready and you can continue straight to your dashboard.
                </p>
                <p className="text-sm font-medium text-emerald-700 mb-8">{successMessage}</p>
                <Link
                  to={PATHS.account}
                  className="inline-block w-full bg-[#2b3543] text-white py-4 rounded-xl font-bold tracking-wide hover:bg-[#1e2631] transition-colors shadow-sm"
                >
                  PROCEED TO ACCOUNT
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-10">
                  <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">Create an Account</h1>
                  <p className="text-slate-500 font-medium">Join thousands of creators building their audience.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        First Name <span className="text-slate-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:ring-1 transition-all ${
                          errors.firstName
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-200 focus:border-slate-800 focus:ring-slate-800'
                        }`}
                      />
                      {errors.firstName && <p className="text-xs font-bold text-red-500 mt-1">{errors.firstName}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        Last Name <span className="text-slate-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:ring-1 transition-all ${
                          errors.lastName
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-200 focus:border-slate-800 focus:ring-slate-800'
                        }`}
                      />
                      {errors.lastName && <p className="text-xs font-bold text-red-500 mt-1">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Email <span className="text-slate-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      className={`w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:ring-1 transition-all ${
                        errors.email
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-200 focus:border-slate-800 focus:ring-slate-800'
                      }`}
                    />
                    {errors.email && <p className="text-xs font-bold text-red-500 mt-1">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Password <span className="text-slate-400">*</span>
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                      className={`w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:ring-1 transition-all tracking-widest ${
                        errors.password
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-200 focus:border-slate-800 focus:ring-slate-800'
                      }`}
                    />
                    {errors.password ? (
                      <p className="text-xs font-bold text-red-500 mt-1">{errors.password}</p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">Must be at least 8 characters.</p>
                    )}
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#2b3543] text-white py-4 rounded-xl font-bold tracking-wide hover:bg-[#1e2631] transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center h-[56px]"
                    >
                      {isSubmitting ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        'CREATE ACCOUNT'
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-8 text-center">
                  <p className="text-sm font-medium text-slate-500">
                    Already have an account?{' '}
                    <Link to={PATHS.login} className="font-bold text-slate-900 hover:underline transition-colors">
                      Log in
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
